// Apolo IDE — useOrchestrator hook (Sprint v4 Epic S3 WS S3.4).
//
// Polls /api/agents/dispatch/:id for live progress and exposes a `kill`
// action that fires POST /api/agents/dispatch/:id/kill. The hook stops
// polling automatically when the dispatch reaches a terminal status so a
// long-lived component does not burn cycles.
//
// API:
//   useOrchestrator(dispatchId?, options?)
//   options.pollIntervalMs   — default 1500
//   options.fetcher          — apiFetch-shaped function (for tests)
//   options.killer           — apiFetch-shaped function (for tests)
//   options.autoStart        — default true; if false caller calls start()
//
// Returns:
//   { dispatch, runs, progress, phase, error, busy, kill, refresh, start, stop }
//
// `phase` is normalized from `dispatch.status`:
//   'idle' | 'pending' | 'running' | 'completed' | 'failed' | 'cancelled'

import { useCallback, useEffect, useRef, useState } from 'react';
import { apiFetch } from '../lib/api.js';

const DEFAULT_POLL_MS = 1500;
const TERMINAL = new Set(['completed', 'failed', 'cancelled']);

async function defaultFetcher(dispatchId) {
  return apiFetch(`/api/agents/dispatch/${encodeURIComponent(dispatchId)}`);
}

async function defaultKiller(dispatchId, reason) {
  return apiFetch(`/api/agents/dispatch/${encodeURIComponent(dispatchId)}/kill`, {
    method: 'POST',
    body: reason ? { reason } : {},
  });
}

function emptyProgress() {
  return { total: 0, pending: 0, running: 0, completed: 0, failed: 0, cancelled: 0 };
}

export default function useOrchestrator(dispatchId, options = {}) {
  const fetcher = options.fetcher || defaultFetcher;
  const killer = options.killer || defaultKiller;
  const pollInterval = options.pollIntervalMs || DEFAULT_POLL_MS;
  const autoStart = options.autoStart !== false;

  const [dispatch, setDispatch] = useState(null);
  const [runs, setRuns] = useState([]);
  const [progress, setProgress] = useState(emptyProgress());
  const [phase, setPhase] = useState('idle');
  const [error, setError] = useState(null);
  const [busy, setBusy] = useState(false);
  const [polling, setPolling] = useState(false);

  const timerRef = useRef(null);
  const mountedRef = useRef(true);

  useEffect(() => {
    mountedRef.current = true;
    return () => { mountedRef.current = false; };
  }, []);

  const stop = useCallback(() => {
    if (timerRef.current) {
      clearTimeout(timerRef.current);
      timerRef.current = null;
    }
    setPolling(false);
  }, []);

  const refresh = useCallback(async () => {
    if (!dispatchId) {
      setError('no dispatchId');
      return { ok: false, error: 'no dispatchId' };
    }
    setBusy(true);
    setError(null);
    try {
      const res = await fetcher(dispatchId);
      if (!res.ok) {
        const m = (res.data && res.data.error) || res.error || 'fetch failed';
        if (mountedRef.current) {
          setError(m);
          setPhase('failed');
        }
        return { ok: false, error: m, status: res.status };
      }
      const data = res.data || res;
      if (!mountedRef.current) return { ok: true, data };
      setDispatch(data.dispatch || null);
      setRuns(Array.isArray(data.runs) ? data.runs : []);
      setProgress(data.progress || emptyProgress());
      const status = (data.dispatch && data.dispatch.status) || 'idle';
      setPhase(status);
      return { ok: true, data, terminal: TERMINAL.has(status) };
    } catch (err) {
      const m = err && err.message ? err.message : String(err);
      if (mountedRef.current) {
        setError(m);
        setPhase('failed');
      }
      return { ok: false, error: m };
    } finally {
      if (mountedRef.current) setBusy(false);
    }
  }, [dispatchId, fetcher]);

  const tick = useCallback(async () => {
    const r = await refresh();
    if (!mountedRef.current) return;
    if (r.terminal) {
      stop();
      return;
    }
    if (timerRef.current === null) return; // stopped during await
    timerRef.current = setTimeout(tick, pollInterval);
  }, [refresh, stop, pollInterval]);

  const start = useCallback(() => {
    if (!dispatchId) return;
    if (timerRef.current) clearTimeout(timerRef.current);
    setPolling(true);
    timerRef.current = setTimeout(tick, 0); // fire immediately
  }, [dispatchId, tick]);

  // Auto-start when dispatchId becomes available.
  useEffect(() => {
    if (!dispatchId || !autoStart) return undefined;
    start();
    return () => {
      if (timerRef.current) {
        clearTimeout(timerRef.current);
        timerRef.current = null;
      }
    };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [dispatchId, autoStart]);

  const kill = useCallback(async (reason) => {
    if (!dispatchId) {
      const m = 'no dispatchId';
      setError(m);
      return { ok: false, error: m };
    }
    setBusy(true);
    setError(null);
    try {
      const res = await killer(dispatchId, reason);
      if (!res.ok) {
        const m = (res.data && res.data.error) || res.error || 'kill failed';
        if (mountedRef.current) setError(m);
        return { ok: false, error: m, status: res.status };
      }
      // Refresh so the renderer reflects the cancellation right away.
      stop();
      await refresh();
      return { ok: true, data: res.data || res };
    } catch (err) {
      const m = err && err.message ? err.message : String(err);
      if (mountedRef.current) setError(m);
      return { ok: false, error: m };
    } finally {
      if (mountedRef.current) setBusy(false);
    }
  }, [dispatchId, killer, stop, refresh]);

  return {
    // state
    dispatch,
    runs,
    progress,
    phase,
    error,
    busy,
    polling,
    isTerminal: TERMINAL.has(phase),
    // actions
    kill,
    refresh,
    start,
    stop,
  };
}
